import type { PromoCode } from '@customerio/hydra-ui-api';
import { startupCohort, type StartupCohortDetails } from '@/services/billing/startup-coupons';
import type { BillingCoupon, BillingSubscription } from '@/services/billing';

/** What the coupon banner states, or null when the account has no discount to explain. */
export type CouponBannerModel = {
  coupon: BillingCoupon | undefined;
  planName: string;
  /** When the coupon ends, as unix seconds. */
  couponEndsAt: number | undefined;
  /** The Startup-program cohort the coupon puts the account in, or null for every other coupon. */
  startupCohort: StartupCohortDetails | null;
  /** Whether the coupon corresponds to a redeemed promo code, which the copy announces differently. */
  isPromoCode: boolean;
};

export type CouponBannerInput = {
  isSubscriptionActive: boolean;
  coupon: BillingCoupon | undefined;
  subscription: BillingSubscription | undefined;
  planName: string;
  couponEndsAt: number | undefined;
  promoCodes: PromoCode[] | undefined;
};

/**
 * Whether the applied coupon came from a redeemed promo code, which the banner announces differently
 * ("You're receiving a discount!") from a sales-negotiated coupon.
 *
 * Matched by code against the account's redeemed promo codes. Only Ordway coupons carry a `code`, so
 * a legacy-Stripe coupon is never a promo code — consistent with promo redemption being an
 * Ordway-plan mechanism.
 */
export function isPromoCoupon(coupon: BillingCoupon | undefined, promoCodes: PromoCode[] | undefined): boolean {
  if (coupon == null || !('code' in coupon)) {
    return false;
  }
  return (promoCodes ?? []).some((promoCode) => promoCode.code === coupon.code);
}

/** Resolves what the banner states, or null when it must stay hidden. */
export function buildCouponBanner(input: CouponBannerInput): CouponBannerModel | null {
  if (!input.isSubscriptionActive) {
    return null;
  }

  // A Startup cohort reads the subscription's coupon code, which the billing profile carries
  // whether or not the request asked for the coupon itself.
  const cohort = startupCohort(input.coupon, input.subscription);
  if (cohort == null && input.coupon == null) {
    return null;
  }

  return {
    coupon: input.coupon,
    planName: input.planName,
    couponEndsAt: input.couponEndsAt,
    startupCohort: cohort,
    isPromoCode: isPromoCoupon(input.coupon, input.promoCodes),
  };
}
